import React, { useState, useMemo } from "react";
import {
  ToolShell, Field, UnitNumber, Select, Result,
  npshAvailable, hazenWilliamsHeadLoss,
  toBase, fromBase, fmt, UNITS,
  FittingsPicker, fittingsEquivalentLength, fittingsSummary,
  SpecCard, SpecValue,
} from "./_shared";

/**
 *  NPSH AVAILABLE  (corrected)
 *  --------------------------------------------------------------
 *  NPSHa = (Pₐ − Pᵥ) / ρg  ±  static head  −  friction loss
 *
 *  Pump-room reality: nobody walks in knowing the suction-line
 *  friction loss in metres.  They know:
 *    · pipe internal diameter
 *    · pipe length (and an equivalent length for fittings)
 *    · pipe material (→ Hazen-Williams C)
 *    · the duty flow
 *
 *  So we compute the friction loss from those inputs using
 *  Hazen-Williams, surface the result, and feed it into the NPSHa
 *  formula.  The user can still see the friction number we used.
 *  --------------------------------------------------------------
 */

const VAPOUR_M = { 10:0.13, 15:0.17, 20:0.24, 25:0.32, 30:0.43, 35:0.57, 40:0.75, 50:1.26, 60:2.03, 70:3.18 };
const TEMPS = Object.keys(VAPOUR_M).map((t) => ({ value: t, label: `${t} °C` }));

const SUCTION_MATERIALS = [
  { value: "150",  label: "HDPE / Polyethylene · C = 150" },
  { value: "150b", label: "PVC · C = 150" },
  { value: "140",  label: "Copper · C = 140" },
  { value: "130",  label: "Cast iron (cement-lined) · C = 130" },
  { value: "120",  label: "Galvanised steel · C = 120" },
  { value: "100",  label: "Steel (older / mild scale) · C = 100" },
];

function atmosphericAt(altM) { return 10.33 - (altM || 0) * 0.00115; }

export default function NPSHCalculator() {
  // Environment
  const [altVal, setAltVal] = useState(800);
  const [altUnit, setAltUnit] = useState("m");
  const [tempC, setTempC] = useState("20");

  // Suction geometry
  const [suctionType, setSuctionType] = useState("flooded");
  const [staticVal, setStaticVal] = useState(2);
  const [staticUnit, setStaticUnit] = useState("m");

  // Suction line — the bit the user actually has on a tape measure
  const [pipeMat, setPipeMat] = useState("150");
  const [idVal, setIdVal] = useState(80);
  const [idUnit, setIdUnit] = useState("mm");
  const [lenVal, setLenVal] = useState(6);          // typical short suction run
  const [lenUnit, setLenUnit] = useState("m");
  const [fittings, setFittings] = useState({});     // { fittingId: qty }
  const [flowVal, setFlowVal] = useState(5);        // L/s
  const [flowUnit, setFlowUnit] = useState("Lps");

  // Pump curve
  const [reqVal, setReqVal] = useState(3);
  const [reqUnit, setReqUnit] = useState("m");

  // → SI
  const altitude_m = toBase("length_big", altVal, altUnit);
  const static_m   = toBase("length_big", staticVal, staticUnit);
  const id_m       = toBase("length_small", idVal, idUnit) / 1000;
  const len_m      = toBase("length_big", lenVal, lenUnit);
  const eqlen_m    = fittingsEquivalentLength(fittings, id_m);
  const total_len_m = len_m + eqlen_m;
  const flow_m3s   = toBase("flow", flowVal, flowUnit);
  const required_m = toBase("length_big", reqVal, reqUnit);
  const C = parseFloat(pipeMat);

  // Compute friction loss internally
  const friction_m = useMemo(
    () => hazenWilliamsHeadLoss({ flow_m3s, id_m, length_m: total_len_m, C }) ?? 0,
    [flow_m3s, id_m, total_len_m, C]
  );

  // Pipe velocity — flag if the suction line is undersized
  const v_mps = id_m > 0
    ? flow_m3s / (Math.PI * id_m * id_m / 4)
    : null;
  // Industry rule of thumb: suction velocity ≤ 1.5 m/s to keep NPSH losses low.
  const vStatus = v_mps == null ? undefined
                : v_mps > 2.0   ? "alert"
                : v_mps > 1.5   ? "warn"
                :                 "ok";
  const vNote = v_mps == null ? null
              : v_mps > 2.0 ? "Above 2.0 m/s — suction line is undersized; increase pipe ID to drop friction & cavitation risk."
              : v_mps > 1.5 ? "Above 1.5 m/s — high for a suction line. Consider one size up."
              :               "Within the ≤ 1.5 m/s suction-line target.";

  const Pa = atmosphericAt(altitude_m);
  const Pv = VAPOUR_M[tempC] ?? 0.24;
  const sign = suctionType === "flooded" ? 1 : -1;

  const NPSHa = useMemo(() => npshAvailable({
    atm_m: Pa, vap_m: Pv, static_m: sign * static_m, friction_m,
  }), [Pa, Pv, sign, static_m, friction_m]);

  const margin = NPSHa - required_m;
  const status = margin < 0 ? "alert" : margin < 0.5 ? "warn" : "ok";
  const note = margin < 0
    ? `Below the pump's NPSH-required by ${fmt(Math.abs(margin))} m — cavitation expected. Up-size suction or shorten run.`
    : margin < 0.5
    ? `Only ${fmt(margin)} m above NPSH-required. Marginal — aim for ≥ 0.5 m safety margin.`
    : `${fmt(margin)} m above NPSH-required. Safe operating margin.`;

  return (
    <ToolShell
      slug="npsh-available"
      title="NPSH available"
      oneLiner="Net Positive Suction Head available at the pump inlet, compared to the pump's NPSH-required. Suction-line friction loss is computed from your pipe geometry."
      onExport={() => ({
        inputs: [
          { label: "Site altitude", value: altVal, unit: altUnit },
          { label: "Water temperature", value: `${tempC} °C` },
          { label: "Suction arrangement", value: suctionType === "flooded" ? "Flooded suction" : "Suction lift" },
          { label: "Static head", value: staticVal, unit: staticUnit },
          { label: "Suction pipe material", value: SUCTION_MATERIALS.find((m) => m.value === pipeMat)?.label ?? pipeMat },
          { label: "Suction pipe internal Ø", value: idVal, unit: UNITS.length_small.labels[idUnit] },
          { label: "Suction pipe length", value: lenVal, unit: lenUnit },
          { label: "Equivalent length (fittings)", value: fmt(eqlen_m, 2), unit: "m" },
          ...fittingsSummary(fittings).map((f) => ({ label: `· ${f.label}`, value: `× ${f.qty}` })),
          { label: "Flow rate", value: flowVal, unit: UNITS.flow.labels[flowUnit] },
          { label: "Pump's NPSH-required", value: reqVal, unit: reqUnit },
        ],
        results: [
          { label: "Computed friction loss", value: fmt(friction_m), unit: "m",
            alts: [{ value: fmt(fromBase("length_big", friction_m, "ft")), unit: "ft" }],
            note: `Hazen-Williams over ${fmt(total_len_m, 1)} m total length (incl. ${fmt(eqlen_m, 1)} m for fittings).` },
          { label: "Suction velocity", value: fmt(v_mps), unit: "m/s",
            alts: v_mps !== null ? [{ value: fmt(fromBase("velocity", v_mps, "fps")), unit: "ft/s" }] : null,
            status: vStatus, note: vNote },
          { label: "NPSH available", value: fmt(NPSHa), unit: "m",
            alts: [{ value: fmt(fromBase("length_big", NPSHa, "ft")), unit: "ft" }] },
          { label: "Margin vs required",
            value: `${margin >= 0 ? "+" : ""}${fmt(margin)}`, unit: "m",
            alts: [{ value: `${margin >= 0 ? "+" : ""}${fmt(fromBase("length_big", margin, "ft"))}`, unit: "ft" }],
            status, note },
        ],
        notes: [
          "Formula: NPSHa = (Pₐ − Pᵥ) / ρg ± static head − friction loss.",
          "Friction loss computed via Hazen-Williams from suction pipe ID, length (incl. fitting equivalents) and flow.",
        ],
        spec: {
          title: "NPSH specification",
          body: `At <strong>${tempC} °C</strong>, altitude <strong>${altVal} ${altUnit}</strong>, ${suctionType === "flooded" ? "flooded" : "lift"} suction of <strong>${staticVal} ${staticUnit}</strong>, NPSHa = <strong>${fmt(NPSHa, 2)} m</strong>${margin >= 0.5 ? ` — safe operating margin of <strong>+${fmt(margin, 2)} m</strong> above the pump's NPSHr of <strong>${reqVal} ${reqUnit}</strong>.` : ` — only <strong>${fmt(margin, 2)} m</strong> versus the pump's NPSHr of <strong>${reqVal} ${reqUnit}</strong>. ${margin < 0 ? "Cavitation expected — up-size the suction or shorten the run." : "Aim for ≥ 0.5 m safety margin."}`}`,
        },
      })}
    >
      <div className="grid md:grid-cols-2 gap-5">
        {/* Environment */}
        <Field label="Site altitude" hint="Atmospheric pressure drops with elevation.">
          <UnitNumber dim="length_big" value={altVal} onChange={setAltVal} unit={altUnit} onUnitChange={setAltUnit}
            units={["m", "ft"]} testid="npsh-alt" />
        </Field>
        <Field label="Water temperature">
          <Select value={tempC} onChange={setTempC} options={TEMPS} testid="npsh-temp" />
        </Field>

        {/* Geometry */}
        <Field label="Suction arrangement">
          <Select value={suctionType} onChange={setSuctionType} options={[
            { value: "flooded", label: "Flooded (tank above pump)" },
            { value: "lift",    label: "Suction lift (pump above water)" },
          ]} testid="npsh-type" />
        </Field>
        <Field label="Static head" hint="Vertical distance between pump centreline and water surface.">
          <UnitNumber dim="length_big" value={staticVal} onChange={setStaticVal} unit={staticUnit} onUnitChange={setStaticUnit}
            units={["m", "ft"]} testid="npsh-static" />
        </Field>

        {/* Suction line */}
        <Field label="Suction pipe material" span={2}>
          <Select value={pipeMat} onChange={setPipeMat} options={SUCTION_MATERIALS} testid="npsh-mat" />
        </Field>
        <Field label="Suction pipe internal diameter">
          <UnitNumber dim="length_small" value={idVal} onChange={setIdVal} unit={idUnit} onUnitChange={setIdUnit}
            units={["mm", "cm", "m", "inch"]} testid="npsh-id" />
        </Field>
        <Field label="Suction pipe length" hint="Straight pipe between the source water surface and the pump inlet.">
          <UnitNumber dim="length_big" value={lenVal} onChange={setLenVal} unit={lenUnit} onUnitChange={setLenUnit}
            units={["m", "ft"]} testid="npsh-len" />
        </Field>
        <Field label="Flow rate (duty point)">
          <UnitNumber dim="flow" value={flowVal} onChange={setFlowVal} unit={flowUnit} onUnitChange={setFlowUnit}
            units={["Lps", "Lpm", "m3h", "gpm"]} testid="npsh-flow" />
        </Field>

        {/* Fittings — collapsible Crane-TP410 style picker */}
        <FittingsPicker qtyMap={fittings} onChange={setFittings} id_m={id_m} testid="npsh-fittings" />

        {/* Pump curve */}
        <Field label="Pump's NPSH-required" hint="Read off the pump curve at the operating point.">
          <UnitNumber dim="length_big" value={reqVal} onChange={setReqVal} unit={reqUnit} onUnitChange={setReqUnit}
            units={["m", "ft"]} testid="npsh-required" />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Result
          label="Computed friction loss"
          primary={{ value: fmt(friction_m), unit: "m" }}
          alts={[{ value: fmt(fromBase("length_big", friction_m, "ft")), unit: "ft" }]}
          note={`Over ${fmt(total_len_m, 1)} m total length (incl. fittings).`}
          testid="npsh-result-friction"
        />
        <Result
          label="Suction velocity"
          primary={{ value: fmt(v_mps), unit: "m/s" }}
          alts={v_mps !== null ? [{ value: fmt(fromBase("velocity", v_mps, "fps")), unit: UNITS.velocity.labels.fps }] : null}
          status={vStatus}
          note={vNote}
          testid="npsh-result-velocity"
        />
        <Result
          label="NPSH available"
          primary={{ value: fmt(NPSHa), unit: "m" }}
          alts={[{ value: fmt(fromBase("length_big", NPSHa, "ft")), unit: "ft" }]}
          testid="npsh-result-available"
        />
        <Result
          label="Margin vs required"
          primary={{ value: `${margin >= 0 ? "+" : ""}${fmt(margin)}`, unit: "m" }}
          alts={[{ value: `${margin >= 0 ? "+" : ""}${fmt(fromBase("length_big", margin, "ft"))}`, unit: "ft" }]}
          status={status}
          note={note}
          testid="npsh-result-margin"
        />
      </div>

      <SpecCard
        eyebrow="NPSH specification"
        testid="npsh-spec-card"
        footer={status === "alert"
          ? "Cavitation will occur at this duty point. Lower the pump, shorten the suction line, increase suction ID, or specify a pump with lower NPSHr."
          : status === "warn"
            ? "Margin is below the recommended 0.5 m safety band — engineer accordingly."
            : null}
      >
        At <SpecValue>{tempC} °C</SpecValue>, altitude{" "}
        <SpecValue>{altVal} {altUnit}</SpecValue>,{" "}
        {suctionType === "flooded" ? "flooded" : "lift"} suction of{" "}
        <SpecValue>{staticVal} {staticUnit}</SpecValue>, NPSHa ={" "}
        <SpecValue>{fmt(NPSHa, 2)} m</SpecValue>{" "}
        {margin >= 0.5
          ? <>— safe operating margin of <SpecValue>+{fmt(margin, 2)} m</SpecValue> above the pump's NPSHr of <SpecValue>{reqVal} {reqUnit}</SpecValue>.</>
          : <>— only <SpecValue>{fmt(margin, 2)} m</SpecValue> versus the pump's NPSHr of <SpecValue>{reqVal} {reqUnit}</SpecValue>.</>}
      </SpecCard>
    </ToolShell>
  );
}
